import React, { useEffect } from 'react'
import { useQuery } from 'react-apollo'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import Loader from '../loader'
import { findConfigValue } from '../../lib/config'
import {
  MAINTENANCE_MODE,
  MAINTENANCE_MODE_BACKGROUND,
  MAINTENANCE_MODE_POLL_INTERVAL,
  MAINTENANCE_MODE_MESSAGE
} from '../../constants'
import { disableMaintenanceMode } from '../../actions'
import { useConfig } from '../../hooks/use-config'
import MAINTENANCE_MODE_QUERY from '../../../graphql/queries/maintenance-mode.gql'
import Imgix from '../imgix'
import neonLogo from '../../../images/neon/neon-logo-yellow.svg'
import styles from './maintenance.css'

/*
** The maintenance page handles two scenarios:
** - localMaintMode is enabled when any request returns a MAINT_MODE_ENABLED error.
** - remoteMaintMode is enabled when MAINTENANCE_MODE_QUERY returns maintenanceMode = true.
** While maintenance mode is active (local or remote), the component polls
** MAINTENANCE_MODE_QUERY every pollInterval (e.g. 5 minutes).
** Once the API returns maintenanceMode = false, stop polling and disable maintenance mode.
*/
function Maintenance({ children }) {
  const history = useHistory()
  const dispatch = useDispatch()
  const { appConfig } = useConfig()
  const localMaintMode = useSelector(state => state.maintenance.mode)
  const message = useSelector(state => state.maintenance.message)

  const {
    data: maintenanceModeQueryData, loading: maintenanceModeQueryLoading, startPolling, stopPolling
  } = useQuery(MAINTENANCE_MODE_QUERY, {
    variables: {
      platform: 'Web'
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })

  const remoteMaintMode = maintenanceModeQueryData?.maintenanceMode

  // pollInterval = seconds and apollo is interpreted in ms
  // e.g. pollInterval = 5 * 60 * 1000 = 5 minutes
  const pollInterval = findConfigValue(MAINTENANCE_MODE.POLL_INTERVAL, appConfig) * 1000 || MAINTENANCE_MODE_POLL_INTERVAL
  const background = findConfigValue(MAINTENANCE_MODE.BACKGROUND, appConfig) || MAINTENANCE_MODE_BACKGROUND

  useEffect(() => {
    // Start polling when maintenance mode is turned on
    if (localMaintMode || remoteMaintMode) {
      startPolling(pollInterval)
      history.replace('/')
    }

    // Stop polling when the server reports maintenanceMode = false,
    // previously it was either returning error or remoteMaintMode was true.
    // After disabling maintenance locally, polling is no longer needed.
    if (!maintenanceModeQueryLoading && remoteMaintMode === false) {
      dispatch(disableMaintenanceMode())
      stopPolling()
    }
  }, [localMaintMode, pollInterval, maintenanceModeQueryData, maintenanceModeQueryLoading])

  if (maintenanceModeQueryLoading) {
    return <Loader />
  }

  if (localMaintMode || remoteMaintMode) {
    return (
      <div className={styles.maintenanceWrapper}>
        <Imgix className={styles.imgixBackground} src={background} background />

        <div className={styles.maintenance}>
          <img className={styles.logo} src={neonLogo} alt="Neon Logo" />

          <h1 className={styles.title}>
            Under Maintenance
          </h1>

          <p className={styles.message}>{message || MAINTENANCE_MODE_MESSAGE}</p>
        </div>
      </div>
    )
  }

  return children
}

export default Maintenance
